/**
 * StoryEdge — the cream custom edge.
 *
 * A smooth-step connector whose styling encodes the route's semantic flavour
 * (`StoryEdgeKind`):
 *   - all route kinds     → solid one-way arrows;
 *   - "choice"            → solid rose, labelled with the option text;
 *   - "end"               → solid coral into the ending node;
 *   - "next"              → solid neutral.
 *
 * The optional label (option text, gate condition, ending type) rides in a
 * small cream pill at the path midpoint via EdgeLabelRenderer.
 */
import { memo } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  type EdgeProps,
} from "@xyflow/react";
import type { StoryEdgeKind } from "../../ls/build-graph.js";

/** Data carried on each ReactFlow edge. */
export interface StoryEdgeData {
  /** Semantic flavour — drives colour + dash. */
  readonly kind: StoryEdgeKind;
  [key: string]: unknown;
}

function isDashed(kind: StoryEdgeKind): boolean {
  void kind;
  return false;
}

function StoryEdgeImpl({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  label,
  data,
}: EdgeProps) {
  const kind = (data as StoryEdgeData | undefined)?.kind ?? "next";
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 14,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        className={`lc-canvas-edge lc-canvas-edge--${kind}${isDashed(kind) ? " is-dashed" : ""}`}
      />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className={`lc-canvas-edge-label lc-canvas-edge-label--${kind}`}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

export const StoryEdge = memo(StoryEdgeImpl);
